import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AUTO_DISMISS_MS, useToast } from '@spotify/confidence-core-react';
import {
  useAccountBillingQuery,
  useAccountSetupQuery,
} from '@spotify/confidence-graphql';

export const CHECKOUT_PARAM = 'checkout';
export const CHECKOUT_SUCCESS = 'success';

export const useRefreshBillingAfterCheckout = (): void => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutSucceeded =
    searchParams.get(CHECKOUT_PARAM) === CHECKOUT_SUCCESS;
  const hasRefreshedRef = useRef(false);

  const { refetch: refetchBilling } = useAccountBillingQuery({
    fetchPolicy: 'network-only',
    skip: !checkoutSucceeded,
  });
  const { refetch: refetchAccount } = useAccountSetupQuery({
    fetchPolicy: 'network-only',
    skip: !checkoutSucceeded,
  });

  useEffect(() => {
    if (!checkoutSucceeded || hasRefreshedRef.current) return;
    hasRefreshedRef.current = true;

    void Promise.all([refetchBilling(), refetchAccount()])
      .then(() => {
        toast.post({
          title: 'Purchase successful',
          status: 'success',
          timeout: AUTO_DISMISS_MS,
        });
      })
      .finally(() => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete(CHECKOUT_PARAM);
        setSearchParams(nextSearchParams, { replace: true });
      });
  }, [
    checkoutSucceeded,
    refetchAccount,
    refetchBilling,
    searchParams,
    setSearchParams,
    toast,
  ]);
};
